import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';

import _ from 'lodash';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { Gallery } from 'common/gallery/Gallery';
import {
    MediaLimitation,
    MediaUrlValidation,
    PublishPostType,
    SelectedSource,
    SourceName,
} from 'social/publisher/Publisher.types';
import { usePublisherDispatch } from 'social/publisher/PublisherContext';
import { fetchValidateMediaUrl } from 'social/publisher/PublisherFetchers';
import { getNumOfSelectedSources } from 'social/publisher/util/Util';
import { MAX_MEDIA_BY_SOURCE } from 'social/publisher/validator/Validator';

import 'social/publisher/media-validation-warning/MediaValidationWarning.scss';

interface Props {
    tenantId: number | undefined;
    media: any[];
    mediaValidation: MediaUrlValidation[];
    selectedSources: SelectedSource;
    responseEditorRef: any;
    customizeSourceKey: SourceName | 'ALL_SOURCES';
}

const MEDIA_LIMITATIONS: Array<MediaLimitation> = [
    {
        source: 'FACEBOOK',
        summary: 'FACEBOOK_MEDIA_RESTRICTIONS',
        image: [
            'FACEBOOK_IMAGE_COUNT_RESTRICTIONS',
            'FACEBOOK_IMAGE_FORMAT_RESTRICTIONS',
            'FACEBOOK_IMAGE_SIZE_RESTRICTIONS',
            'FACEBOOK_IMAGE_RESOLUTION_RESTRICTIONS',
        ],
        video: [
            'FACEBOOK_VIDEO_FORMAT_RETRICTIONS',
            'FACEBOOK_VIDEO_SIZE_RESTRICTIONS',
            'FACEBOOK_VIDEO_LENGTH_RESTRICTIONS',
        ],
    },
    {
        source: 'TWITTER',
        summary: 'TWITTER_MEDIA_RESTRICTIONS',
        image: [
            'TWITTER_IMAGE_COUNT_RESTRICTIONS',
            'TWITTER_IMAGE_FORMAT_RESTRICTIONS',
            'TWITTER_IMAGE_SIZE_RESTRICTIONS',
            'TWITTER_IMAGE_RESOLUTION_RESTRICTIONS',
        ],
        video: [
            'TWITTER_VIDEO_FORMAT_RESTRICTIONS',
            'TWITTER_VIDEO_SIZE_RESTRICTIONS',
            'TWITTER_VIDEO_RESOLUTION_RESTRICTIONS',
            'TWITTER_VIDEO_ASPECT_RATIO_RESTRICTIONS',
            'TWITTER_VIDEO_DURATION_RESTRICTIONS',
        ],
    },
    {
        source: 'INSTAGRAM',
        summary: 'INSTAGRAM_MEDIA_RESTRICTIONS',
        image: [
            'INSTAGRAM_IMAGE_COUNT_RESTRICTIONS',
            'INSTAGRAM_IMAGE_FORMAT_RESTRICTIONS',
            'INSTAGRAM_IMAGE_SIZE_RESTRICTIONS',
            'INSTAGRAM_IMAGE_ASPECT_RATIO_RESTRICTIONS',
            'INSTAGRAM_IMAGE_RESOLUTION_RESTRICTIONS',
        ],
        video: [
            'INSTAGRAM_VIDEO_COUNT_RESTRICTIONS',
            'INSTAGRAM_VIDEO_FORMAT_RESTRICTIONS',
            'INSTAGRAM_VIDEO_SIZE_RESTRICTIONS',
            'INSTAGRAM_VIDEO_LENGTH_RESTRICTIONS',
            'INSTAGRAM_VIDEO_ASPECT_RATIO_RESTRICTIONS',
            'INSTAGRAM_VIDEO_WIDTH_RESTRICTIONS',
        ],
    },
    {
        source: 'GOOGLE_PLACES',
        summary: 'GOOGLE_PLACES_MEDIA_RESTRICTIONS',
        image: [
            'GOOGLE_PLACES_IMAGE_COUNT_RESTRICTIONS',
            'GOOGLE_PLACES_IMAGE_SIZE_RESTRICTIONS',
            'GOOGLE_PLACES_IMAGE_RESOLUTION_RESTRICTIONS',
        ],
    },
    {
        source: 'LINKEDIN',
        summary: 'LINKEDIN_MEDIA_RESTRICTIONS',
        image: [
            'LINKEDIN_IMAGE_COUNT_RESTRICTIONS',
            'LINKEDIN_IMAGE_FORMAT_RESTRICTIONS',
            'LINKEDIN_IMAGE_SIZE_RESTRICTIONS',
            'LINKEDIN_IMAGE_RESOLUTION_RESTRICTIONS',
        ],
        video: [
            'LINKEDIN_VIDEO_FORMAT_RESTRICTIONS',
            'LINKEDIN_VIDEO_SIZE_RESTRICTIONS',
            'LINKEDIN_VIDEO_LENGTH_RESTRICTIONS',
            'LINKEDIN_VIDEO_ASPECT_RATIO_RESTRICTIONS',
            'LINKEDIN_VIDEO_WIDTH_RESTRICTIONS',
            'LINKEDIN_VIDEO_HEIGHT_RESTRICTIONS',
        ],
    },
    {
        source: 'YOUTUBE',
        summary: 'YOUTUBE_MEDIA_RESTRICTIONS',
        video: ['YOUTUBE_VIDEO_FORMAT_RESTRICTIONS', 'YOUTUBE_VIDEO_SIZE_RESTRICTIONS'],
    },
    {
        source: 'IG_STORIES',
        summary: 'IG_STORIES_MEDIA_RESTRICTIONS',
        image: [
            'IG_STORIES_IMAGE_COUNT_RESTRICTIONS',
            'IG_STORIES_IMAGE_FORMAT_RESTRICTIONS',
            'IG_STORIES_IMAGE_SIZE_RESTRICTIONS',
            'IG_STORIES_IMAGE_ASPECT_RATIO_RESTRICTIONS',
            'IG_STORIES_IMAGE_RESOLUTION_RESTRICTIONS',
        ],
        video: [
            'IG_STORIES_VIDEO_COUNT_RESTRICTIONS',
            'IG_STORIES_VIDEO_FORMAT_RESTRICTIONS',
            'IG_STORIES_VIDEO_SIZE_RESTRICTIONS',
            'IG_STORIES_VIDEO_ASPECT_RATIO_RESTRICTIONS',
            'IG_STORIES_VIDEO_LENGTH_RESTRICTIONS',
        ],
    },
    {
        source: 'IG_REELS',
        summary: 'IG_REELS_MEDIA_RESTRICTIONS',
        video: [
            'IG_REELS_VIDEO_COUNT_RESTRICTIONS',
            'IG_REELS_VIDEO_FORMAT_RESTRICTIONS',
            'IG_REELS_VIDEO_SIZE_RESTRICTIONS',
            'IG_REELS_VIDEO_ASPECT_RATIO_RESTRICTIONS',
            'IG_REELS_VIDEO_LENGTH_RESTRICTIONS',
            'IG_REELS_VIDEO_RESOLUTION_RESTRICTIONS',
        ],
    },
    {
        source: 'TIKTOK',
        summary: 'TIKTOK_MEDIA_RESTRICTIONS',
        video: [
            'TIKTOK_VIDEO_COUNT_RESTRICTIONS',
            'TIKTOK_VIDEO_FORMAT_RESTRICTIONS',
            'TIKTOK_VIDEO_SIZE_RESTRICTIONS',
            'TIKTOK_VIDEO_RESOLUTION_RESTRICTIONS',
            'TIKTOK_VIDEO_LENGTH_RESTRICTIONS',
        ],
    },
];

export const MediaValidationWarning: FunctionComponent<Props> = ({
    tenantId,
    media,
    mediaValidation,
    selectedSources,
    responseEditorRef,
    customizeSourceKey,
}) => {
    const dispatch = usePublisherDispatch();
    const { t } = useTranslation(['social/publisher']);
    const [showLoading, setShowLoading] = useState(false);
    const [isModalShowing, setIsModalShowing] = useState<boolean>(false);
    const [selectedSourceMediaLimitations, setSelectedSourceMediaLimitations] = useState<MediaLimitation[]>([]);
    const isAnySourceSelected = getNumOfSelectedSources(selectedSources) > 0;

    const handleShowModal = (): void => {
        setIsModalShowing(true);
    };

    const handleCloseModal = (): void => {
        setIsModalShowing(false);
    };

    const buildSourceErrorsList = (errorsArray: string[]): JSX.Element => {
        return (
            <>
                {Array.isArray(errorsArray) &&
                    errorsArray.map((error) => {
                        return (
                            <li key={error} className="media-validation-errors-source-list-item">
                                {error}
                            </li>
                        );
                    })}
            </>
        );
    };

    const validateAllFiles = useCallback(async () => {
        let allValidation: MediaUrlValidation[] = [];
        // Ensure we only validate media we haven't cached validation already
        const mediaToValidate = media.reduce((output, media) => {
            if (media && media.url) {
                const existingValidation = _.find(mediaValidation, (validation) => {
                    return validation.url === media.url;
                });

                if (!existingValidation) {
                    output.push(media.url);
                }
            }
            return output;
        }, []);

        if (mediaToValidate.length > 0) {
            setShowLoading(true);

            dispatch({
                type: 'add-promises-in-transit',
                payload: {
                    value: 'MEDIA_VALIDATION',
                },
            });

            try {
                const [newValidation, newStoriesValidation, newReelsValidation] = await Promise.all([
                    fetchValidateMediaUrl(tenantId, mediaToValidate, PublishPostType.REGULAR),
                    fetchValidateMediaUrl(tenantId, mediaToValidate, PublishPostType.STORY),
                    fetchValidateMediaUrl(tenantId, mediaToValidate, PublishPostType.REEL),
                ]);

                allValidation = allValidation.concat(mediaValidation, newValidation);
                allValidation = allValidation.concat(newStoriesValidation);
                allValidation = allValidation.concat(newReelsValidation);

                dispatch({
                    type: 'set-media-validation',
                    payload: {
                        value: allValidation,
                    },
                });
            } catch (e) {
                // TODO: Offer user ability to re-attempt validation.
                console.log(e);
            } finally {
                dispatch({
                    type: 'remove-promises-in-transit',
                    payload: {
                        value: 'MEDIA_VALIDATION',
                    },
                });

                setShowLoading(false);
            }
        }
    }, [media, mediaValidation, tenantId, dispatch]);

    const validationListDiv: JSX.Element[] = useMemo(() => {
        const validationDisplay: JSX.Element[] = [];
        const handleRemoveMedia = (index: number): void => {
            const newMedia = [...media];

            newMedia.splice(index, 1);

            // Propogates the new media set back to the ResponseEditor
            if (responseEditorRef && responseEditorRef.current && responseEditorRef.current.setMedia) {
                responseEditorRef.current.setMedia(newMedia);
            }

            dispatch({
                type: 'set-media',
                payload: {
                    value: newMedia,
                },
            });

            dispatch({
                type: 'remove-publisher-errors-by-component',
                payload: {
                    value: 'MEDIA',
                },
            });
        };
        const getMatchingMediaValidations = (mediaObj: any) => {
            const validationsBySource: any = {};

            Object.keys(selectedSources).forEach((source) => {
                if (selectedSources[source]) {
                    if (['IG_STORIES', 'FB_STORIES'].includes(source)) {
                        const matchingMediaValidation = _.find(mediaValidation, (validation) => {
                            return (
                                validation.url === mediaObj.url && validation.publishPostType === PublishPostType.STORY
                            );
                        });
                        const matchingMediaSourceValidation =
                            matchingMediaValidation?.sourceValidation[
                                source === 'IG_STORIES' ? 'INSTAGRAM' : 'FACEBOOK'
                            ];

                        if (validationsBySource[source]) {
                            validationsBySource[source].errorMessages = validationsBySource[
                                source
                            ].errorMessages.concat(matchingMediaSourceValidation?.errorMessages);
                            validationsBySource[source].valid =
                                validationsBySource[source].valid && matchingMediaSourceValidation?.valid;
                        } else {
                            validationsBySource[source] = matchingMediaSourceValidation;
                        }
                    } else if (['IG_REELS', 'FB_REELS'].includes(source)) {
                        const matchingMediaValidation = _.find(mediaValidation, (validation) => {
                            return (
                                validation.url === mediaObj.url && validation.publishPostType === PublishPostType.REEL
                            );
                        });
                        const matchingMediaSourceValidation =
                            matchingMediaValidation?.sourceValidation[source === 'IG_REELS' ? 'INSTAGRAM' : 'FACEBOOK'];

                        if (validationsBySource[source]) {
                            validationsBySource[source].errorMessages = validationsBySource[
                                source
                            ].errorMessages.concat(matchingMediaSourceValidation?.errorMessages);
                            validationsBySource[source].valid =
                                validationsBySource[source].valid && matchingMediaSourceValidation?.valid;
                        } else {
                            validationsBySource[source] = matchingMediaSourceValidation;
                        }
                    } else {
                        const matchingMediaValidation = _.find(mediaValidation, (validation) => {
                            return (
                                validation.url === mediaObj.url &&
                                validation.publishPostType === PublishPostType.REGULAR
                            );
                        });
                        const matchingMediaSourceValidation = matchingMediaValidation?.sourceValidation[source];

                        if (validationsBySource[source]) {
                            validationsBySource[source].errorMessages = validationsBySource[
                                source
                            ].errorMessages.concat(matchingMediaSourceValidation?.errorMessages);
                            validationsBySource[source].valid =
                                validationsBySource[source].valid && matchingMediaSourceValidation?.valid;
                        } else {
                            validationsBySource[source] = matchingMediaSourceValidation;
                        }
                    }
                }
            });

            return validationsBySource;
        };

        // Loop through media and build validation display
        media.forEach((mediaObj, mediaIndex) => {
            let isVisible = false;
            const errorDisplay: JSX.Element[] = [];
            const matchingMediaValidations = getMatchingMediaValidations(mediaObj);

            if (Object.keys(matchingMediaValidations).length > 0) {
                // From selected sources, loop through
                _.forIn(matchingMediaValidations, (sourceValidation, key) => {
                    if (
                        sourceValidation &&
                        !sourceValidation.valid &&
                        Array.isArray(sourceValidation.errorMessages) &&
                        sourceValidation.errorMessages.length > 0 &&
                        mediaIndex < MAX_MEDIA_BY_SOURCE[key]
                    ) {
                        // Check if we should show the validations at all (only if it's invalid), and show this part only once
                        if (!isVisible) {
                            isVisible = true;
                        }

                        // All the errors, based by selected sources
                        errorDisplay.push(
                            <div key={key.toLowerCase()} className="media-validation-errors-source">
                                <div
                                    className={`media-validation-errors-source-name media-validation-errors-source-name-${key.toLowerCase()}`}
                                >
                                    {t(key)}
                                </div>
                                <ul className="media-validation-errors-source-list">
                                    {buildSourceErrorsList(sourceValidation.errorMessages)}
                                </ul>
                            </div>,
                        );
                    }
                });
            }

            if (isVisible) {
                validationDisplay.push(
                    <div className="media-validation-group" key={mediaObj.url}>
                        <div className="media-validation-media">
                            <Gallery galleryData={[mediaObj]} displayFileNameOnHover openModalOnMediaClick={false} />
                            <div className="media-validation-options">
                                {/* <Button variant="link" type="button">
                                    {t('EDIT')}
                                </Button> */}
                                <Button
                                    variant="link"
                                    type="button"
                                    onClick={(): void => {
                                        handleRemoveMedia(mediaIndex);
                                    }}
                                >
                                    {t('REMOVE')}
                                </Button>
                            </div>
                        </div>
                        <div className="media-validation-errors">{errorDisplay}</div>
                    </div>,
                );
            }
        });

        return validationDisplay;
    }, [t, media, mediaValidation, selectedSources, responseEditorRef, dispatch]);

    useEffect((): void => {
        validateAllFiles();
    }, [validateAllFiles]);

    useEffect((): void => {
        if (customizeSourceKey === 'ALL_SOURCES') {
            setSelectedSourceMediaLimitations(
                MEDIA_LIMITATIONS.filter((mediaLimitation) => selectedSources[mediaLimitation.source]),
            );
        } else {
            setSelectedSourceMediaLimitations(
                MEDIA_LIMITATIONS.filter((mediaLimitation) => mediaLimitation.source === customizeSourceKey),
            );
        }
    }, [customizeSourceKey, selectedSources]);

    const renderValidationDiv = (): JSX.Element | undefined => {
        let validationDiv;
        const mediaLimitationDiv = (
            <h3 className="media-validation-warning-title initial">
                <i className="icon-image" />
                <Button variant="link" onClick={handleShowModal}>
                    {t('MEDIA_LIMITATIONS')}
                </Button>
            </h3>
        );
        const validationLoadingDiv = (
            <h3 className="media-validation-warning-title loading">
                <i className="icon-spinner" />
                <span>{t('VALIDATING_FILES_ELIPSIS')}</span>
            </h3>
        );
        const validationPassedDiv = (
            <h3 className="media-validation-warning-title passed">
                <i className="icon-check-circle" />
                <Button variant="link" onClick={handleShowModal}>
                    {t('ALL_FILES_PASSED_VALIDATION')}
                </Button>
            </h3>
        );
        const validationFailedDiv = (
            <>
                <h3 className="media-validation-warning-title failed">
                    <i className="icon-exclamation-circle" />
                    <Button variant="link" onClick={handleShowModal}>
                        {t('MEDIA_ERRORS')}
                    </Button>
                </h3>
                {validationListDiv}
            </>
        );

        if (isAnySourceSelected) {
            if (media.length === 0) {
                validationDiv = mediaLimitationDiv;
            } else if (showLoading) {
                validationDiv = validationLoadingDiv;
            } else if (validationListDiv.length === 0) {
                validationDiv = validationPassedDiv;
            } else {
                validationDiv = validationFailedDiv;
            }
        }

        return validationDiv;
    };

    return (
        <div className="r4e-social-media-validation-warning">
            {renderValidationDiv()}
            <Modal show={isModalShowing} onHide={handleCloseModal} className="r4e-social-media-limitations">
                <Modal.Header closeButton>
                    <Modal.Title>{t('MEDIA_LIMITATIONS')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedSourceMediaLimitations.map((mediaLimitation) => (
                        <div key={mediaLimitation.source}>
                            <div className="source-heading">
                                {t('ASSET_REQUIREMENTS', { source: t(mediaLimitation.source) })}
                            </div>
                            {mediaLimitation.image && (
                                <div>
                                    <div className="requirement-heading">{t('IMAGES')}</div>
                                    <ul>
                                        {mediaLimitation.image.map((img) => (
                                            <li key={img}>{t(img)}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {mediaLimitation.video && (
                                <div>
                                    <div className="requirement-heading">{t('VIDEO')}</div>
                                    <ul>
                                        {mediaLimitation.video.map((vid) => (
                                            <li key={vid}>{t(vid)}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    ))}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleCloseModal}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
